import type { ReactNode } from "react";
import { BrandLogo } from "@/components/BrandLogo";
import { useTheme } from "@/lib/theme/ThemeProvider";
import { SecureCheckoutBadge } from "@/components/SecureCheckoutBadge";
import type { BrandTheme } from "@/lib/brand-theme";
import { ChatReturnButton } from "./ChatReturnButton";
import { WhatsAppButton } from "./WhatsAppButton";

export type { BrandTheme as ExpressCartBrand } from "@/lib/brand-theme";

export function CenteredMessage({ title, body, detail }: { title: string; body: string; detail?: string }) {
  return (
    <main className="mx-auto max-w-md px-4 py-24 text-center">
      <h1 className="text-xl font-bold text-(--color-text-primary)">{title}</h1>
      <p className="mt-2 text-(--color-text-secondary)">{body}</p>
      {detail && <p className="mt-4 text-xs text-(--color-error)">{detail}</p>}
    </main>
  );
}

interface Props {
  brand: BrandTheme;
  whatsappUrl: string | null;
  /** Set when the shopper came from a WhatsApp chat: shows the way back instead. */
  chatReturnUrl?: string | null;
  children: ReactNode;
}

/**
 * Page frame shared by the brand link (/cart/:slug) and View Cart (/cart)
 * express carts, matching the checkout's: the brand's logo (or name), the
 * secure checkout badge with the light/dark toggle, and "Powered by Nile".
 */
export function ExpressCartLayout({ brand, whatsappUrl, chatReturnUrl, children }: Props) {
  const { resolvedTheme } = useTheme();
  return (
    <div className="min-h-screen bg-(--color-bg-primary) flex flex-col justify-between pb-28 lg:pb-0">
      <div>
        <header className="bg-(--color-surface-primary) border-b border-(--color-surface-border-light)">
          <div className="mx-auto max-w-5xl px-4 py-4">
            <div className="flex items-center justify-between flex-wrap-reverse">
              <BrandLogo brand={brand} />
              <SecureCheckoutBadge />
            </div>
          </div>
        </header>

        {children}
      </div>

      <footer className="hidden lg:flex border-t border-(--color-surface-border-light) p-4 items-center justify-center gap-1.5 text-xs text-(--color-text-muted)">
        <span>Powered by</span>
        <img
          src={resolvedTheme === "light" ? "/images/nile-wordmark-dark.png" : "/images/nile-wordmark-light.png"}
          alt="Nile"
          className="h-3.5 mb-1"
        />
      </footer>

      {chatReturnUrl ? (
        <ChatReturnButton whatsAppUrl={chatReturnUrl} />
      ) : (
        whatsappUrl && <WhatsAppButton href={whatsappUrl} retailerName={brand.name} />
      )}
    </div>
  );
}
